import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_BOTTOM_GRAY } from 'client/components/SrCollapsibleContainer/SrCollapsibleContainer';
import {
  getStepIdsByHouseholdCategory,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_TO_COMPONENT_MAPPING,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_TO_OVERRIDE_FIELD_MAPPING,
} from 'client/components/SrApplicationAssemblyLine/assemblyLineStepIds';

import SrApplicationAssemblyLineContainer from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainer';

const SrApplicationAssemblyLineSteps = ({
  assemblyLine,
  applicant,
  householdApplication,
  reloadAssemblyLineData,
  updateAssemblyLineApplication,
  initialStepExpanded,
  onEditAssemblyLineApplicationFields,
}) => {
  const stepIds = getStepIdsByHouseholdCategory({
    category: householdApplication.category,
    isLeadRenter: householdApplication.userId === assemblyLine.leadRenterId,
  });
  const [stepsExpandedState, setStepsExpandedState] = useState(
    stepIds.reduce((memo, stepId) => ({
      ...memo,
      [stepId]: stepId === initialStepExpanded,
    })),
  );

  const onStepClick = useCallback(
    (stepId) => {
      setStepsExpandedState({
        ...stepsExpandedState,
        [stepId]: !stepsExpandedState[stepId],
      });
    },
    [stepsExpandedState, setStepsExpandedState],
  );

  const stepStatuses = assemblyLine.status.stepsByApplicants?.find(({ renterId }) => {
    return renterId === householdApplication.userId;
  })?.stepStatuses;

  return stepIds.map((stepId) => {
    const stepStatus = stepStatuses.find((stepStatus) => stepStatus.stepId === stepId);

    const StepComponent = SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_TO_COMPONENT_MAPPING[stepId];

    const onEditClick = () =>
      onEditAssemblyLineApplicationFields({
        assemblyLineApplication: householdApplication.assemblyLineApplication,
        fields: [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_TO_OVERRIDE_FIELD_MAPPING[stepId]],
        fieldId: stepId,
      });

    return (
      <SrApplicationAssemblyLineContainer
        key={stepId}
        title={
          StepComponent.getStepTitle
            ? StepComponent.getStepTitle({ assemblyLine })
            : StepComponent.stepTitle
        }
        isExpanded={stepsExpandedState[stepId]}
        status={stepStatus.status}
        borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_BOTTOM_GRAY}
        onClick={() => onStepClick(stepId)}
        onEditClick={stepStatus.isEditable ? onEditClick : null}
      >
        <StepComponent
          assemblyLine={assemblyLine}
          householdApplication={householdApplication}
          reloadAssemblyLineData={reloadAssemblyLineData}
          updateAssemblyLineApplication={updateAssemblyLineApplication}
          applicant={applicant}
          status={stepStatus}
        />
      </SrApplicationAssemblyLineContainer>
    );
  });
};

SrApplicationAssemblyLineSteps.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
  reloadAssemblyLineData: PropTypes.func.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
  initialStepExpanded: PropTypes.string,
  applicant: PropTypes.object.isRequired,
  onEditAssemblyLineApplicationFields: PropTypes.func.isRequired,
};

SrApplicationAssemblyLineSteps.displayName = 'SrApplicationAssemblyLineSteps';

export default SrApplicationAssemblyLineSteps;
